import { ERemoveBaseContentOption } from '@constants';
import { ISection } from '@sdk/contracts';
import { Button, Checkbox, CheckboxChangeEvent, Flex, Modal, Radio, RadioGroupProps } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';

import {
  RadioDescriptionWrapper,
  RadioGroupWrapper,
  RadioTitleWrapper
} from './RemoveBaseContentSection.styles';

import { Icon } from '@/components/Icon';
import { useMutateSection } from '@/store';

type Props = {
  courseId: string;
  section: ISection;
};

const MODAL_WIDTH = 572;

export const RemoveBaseContentSection: React.FC<Props> = ({ courseId, section }) => {
  const { isWorking, removeSection } = useMutateSection();
  const { t } = useTranslation(['CourseTranslations', 'DefaultTranslations']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [deleteOption, setDeleteOption] = useState<ERemoveBaseContentOption>(
    ERemoveBaseContentOption.ONLY_BASE_CONTENT
  );

  const handleDeleteSection = () =>
    removeSection({
      courseId,
      sectionId: section._id,
      removeSyncedSections: deleteOption
    });

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsConfirmed(!isConfirmed);
  };

  const handleRadioChange: RadioGroupProps['onChange'] = e => {
    setDeleteOption(e.target.value);
  };

  return (
    <>
      <Button
        danger
        type="text"
        loading={isWorking}
        onClick={e => {
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        icon={<Icon i="TrashCan" />}>
        {t('CourseTranslations:DELETE_SECTION')}
      </Button>

      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        okText={t('CourseTranslations:DELETE_SECTION')}
        cancelText={t('DefaultTranslations:CANCEL')}
        onOk={handleDeleteSection}
        centered
        width={MODAL_WIDTH}
        okButtonProps={{ disabled: !isConfirmed, loading: isWorking }}
        cancelButtonProps={{ disabled: isWorking }}
        title={t('CourseTranslations:DELETE_SECTION_QUESTION')}
        wrapProps={{ onClick: (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation() }}>
        <Flex vertical gap={24} style={{ paddingTop: 16, paddingBottom: 16 }}>
          <span>
            <Trans
              i18nKey="CourseTranslations:SECTION_IS_BEING_USED_IN_OTHER_COURSES"
              values={{ name: section.name }}
              components={{ bold: <strong /> }}
            />
          </span>

          <Flex vertical gap={16}>
            <RadioTitleWrapper>
              {t('CourseTranslations:DECIDE_HOW_YOU_WANT_TO_DELETE')}
            </RadioTitleWrapper>

            <Radio.Group value={deleteOption} onChange={handleRadioChange}>
              <RadioGroupWrapper>
                <Flex vertical>
                  <Radio value={ERemoveBaseContentOption.ONLY_BASE_CONTENT}>
                    <span>{t('CourseTranslations:ONLY_IN_BASIC_CONTENT')}</span>
                  </Radio>

                  <ul>
                    <li>{t('CourseTranslations:ONLY_IN_BASIC_CONTENT_SECTION_1')}</li>
                    <li>{t('CourseTranslations:ONLY_IN_BASIC_CONTENT_SECTION_2')}</li>
                  </ul>
                </Flex>

                <Flex vertical>
                  <Radio value={ERemoveBaseContentOption.REMOVE_SYNCED_CONTENT}>
                    <span>{t('CourseTranslations:BASIC_CONTENT_AND_OTHER_COURSES')}</span>
                  </Radio>
                  <RadioDescriptionWrapper>
                    {t('CourseTranslations:BASIC_CONTENT_AND_OTHER_COURSES_SECTION_DESCRIPTION')}
                  </RadioDescriptionWrapper>
                </Flex>
              </RadioGroupWrapper>
            </Radio.Group>
          </Flex>

          <Checkbox checked={isConfirmed} onChange={handleCheckboxChange}>
            {t('DefaultTranslations:I_CONFIRM_I_UNDERSTAND')}
          </Checkbox>
        </Flex>
      </Modal>
    </>
  );
};
